import React from "react";
import "./signup.css";
import "./otp-verification.css";
import googleLogo from "../../assets/images/google-logo.svg";
import { AuthUser } from "../../types/auth-types";
import useSignup from "../../hooks/use-signup";
import Toast from "../uiComponents/Toast";
import { isFreeBetaModeEnabled } from "../../utils/beta";

interface SignupProps {
  onAuthSuccess?: (user: AuthUser) => void;
}

const brandLogo = `${process.env.PUBLIC_URL}/impret-icon.svg`;
const isFreeBeta = isFreeBetaModeEnabled();

const Signup: React.FC<SignupProps> = ({ onAuthSuccess }) => {
  const {
    email,
    password,
    loading,
    handleGoogleSignup,
    handleSignup,
    navigateToLogin,
    showOtp,
    handleBackToSignup,
    otp,
    handleOtpChange,
    handleKeyDown,
    handleVerifyEmail,
    handlePaste,
    handleResendOtp,
    toast,
    handleToastClose,
    handleOtpInputRef,
    handleEmailChange,
    handlePasswordChange,
    passwordStrength,
    resendState,
  } = useSignup(onAuthSuccess);

  if (showOtp) {
    return (
      <div className="verification-container">
      <div className="verification-left-panel">
        <div className="brand-logo">
          <img src={brandLogo} alt="Impret" className="brand-logo-mark" />
          Impret
        </div>

          <div className="signup-hero-content">
            <h1 className="hero-title">
              {isFreeBeta
                ? "Free beta access is now open."
                : "Build your first client brand workspace."}
            </h1>
            <p className="hero-subtitle">
              {isFreeBeta
                ? "Verify your email to unlock the full Impret workflow while beta access is free."
                : "Verify your email to start building a brand workspace for recurring content."}
            </p>

            <div style={{ marginTop: "40px" }}>
              <div
                style={{
                  background: "rgba(255,255,255,0.1)",
                  padding: "16px",
                  borderRadius: "12px",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    background: "white",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#6366f1",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  ✓
                </div>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  Save product context once
                </span>
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,0.1)",
                  padding: "16px",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    background: "white",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#6366f1",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  📈
                </div>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  Draft social posts from that context
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="verification-right-panel">
          <button className="back-to-signup" onClick={handleBackToSignup}>
            ← Back to Sign Up
          </button>

          <div className="verification-form-wrapper">
            <div className="email-icon-wrapper">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="email-icon"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
            </div>

            <h2 className="verification-title">Check your email</h2>
            <p className="verification-subtitle">
              We sent a 6-digit verification code to
            </p>
            <p className="verification-email">{email || "user@company.com"}</p>

            <div className="otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  className="otp-input"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={(e) => handlePaste(e, index)}
                  ref={(el) => handleOtpInputRef(index, el)}
                />
              ))}
            </div>

            <button className="verify-button" onClick={handleVerifyEmail} disabled={loading}>
              {loading ? "Verifying..." : "Verify Email"}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                style={{ width: 16, height: 16 }}
              >
                <path
                  fillRule="evenodd"
                  d="M16.403 12.652a3 3 0 000-5.304 3 3 0 00-3.75-3.651 3 3 0 00-4.55 0 3 3 0 00-3.75 3.651 3 3 0 000 5.304 3 3 0 003.75 3.651 3 3 0 004.55 0 3 3 0 003.75-3.651zM10 8a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
                  clipRule="evenodd"
                />
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            <p className="resend-text">
              Didn't receive the email?{" "}
              <span
                className="resend-link"
                onClick={handleResendOtp}
                style={resendState.style}
              >
                {resendState.label}
              </span>
            </p>

            <div className="secure-notice">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="lock-icon"
              >
                <path
                  fillRule="evenodd"
                  d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                  clipRule="evenodd"
                />
              </svg>
              Secure 256-bit encrypted verification
            </div>
          </div>
        </div>

        {toast?.isVisible && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={handleToastClose}
            duration={3000}
          />
        )}
      </div>
    );
  }

  return (
    <div className="signup-container">
      <div className="signup-left-panel">
        <div className="brand-logo">
          <img src={brandLogo} alt="Impret" className="brand-logo-mark" />
          Impret
        </div>

        <div className="signup-hero-content">
          <h1 className="hero-title">
            {isFreeBeta ? "Join the Impret Free Beta" : "Create content for your client brands"}
          </h1>

          <ul className="features-list">
            <li className="feature-item">
              <span className="feature-check">✓</span>
              {isFreeBeta ? "Full workflow unlocked during beta" : "50 AI generations per month"}
            </li>
            <li className="feature-item">
              <span className="feature-check">✓</span>
              Start with one client brand
            </li>
            <li className="feature-item">
              <span className="feature-check">✓</span>
              {isFreeBeta ? "Scheduling, SEO, and growth tools included" : "Weekly content plan and history"}
            </li>
          </ul>

          <div className="signup-info-card">
            <div className="signup-info-label">Start with one client workspace</div>
            <p className="signup-info-text">
              {isFreeBeta
                ? "Add your client website, PDFs, or blogs once. During the free beta, use that context for social posts, SEO drafts, weekly plans, and scheduling."
                : "Free includes one brand, 50 generations per month, one knowledge source, a weekly plan, and content history. Add a client website or document to ground your drafts. Scheduling and SEO drafts are available on Pro and Agency."}
            </p>
            <div className="signup-info-grid">
              <span>Brand context</span>
              <span>Creator Studio</span>
              <span>Weekly plan</span>
              <span>Content history</span>
            </div>
          </div>
        </div>
      </div>

      <div className="signup-right-panel">
        <div className="signup-form-wrapper">
          <div className="form-header">
            <h2 className="form-title">Create your agency workspace</h2>
            <p className="form-subtitle">
              {isFreeBeta
                ? "Free beta access. No credit card required."
                : "Start free with one client brand. No credit card required."}
            </p>
          </div>

          <form onSubmit={handleSignup}>
            <div className="input-group">
              <label className="input-label">Work Email</label>
              <input
                type="email"
                className="input-field"
                placeholder="jane@company.com"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
              />
            </div>

            <div className="input-group">
              <label className="input-label">Password</label>
              <input
                type="password"
                className="input-field"
                placeholder="••••••••"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
              />
              <div className="password-strength">
                <div
                  className="strength-bar"
                  style={{
                    background: passwordStrength.weak ? "#ef4444" : "#e5e7eb",
                  }}
                ></div>
                <div
                  className="strength-bar"
                  style={{
                    background: passwordStrength.medium ? "#f59e0b" : "#e5e7eb",
                  }}
                ></div>
                <div
                  className="strength-bar"
                  style={{
                    background: passwordStrength.good ? "#10b981" : "#e5e7eb",
                  }}
                ></div>
              </div>
              <p className="strength-text">Strength: {passwordStrength.label}</p>
            </div>

            <button type="submit" className="signup-button" disabled={loading}>
              {loading ? "Creating Account..." : "Get Started"}
            </button>
          </form>

          <div className="divider">
            <span>OR</span>
          </div>

          <button className="google-button" onClick={handleGoogleSignup}>
            <img
              src={googleLogo}
              alt="Google"
              style={{ width: 20, height: 20 }}
            />
            Sign up with Google
          </button>

          <p className="footer-text">
            Already have an account?
            <span className="login-link" onClick={navigateToLogin}>
              Log In
            </span>
          </p>

          <p className="terms-text">
            By clicking "Get Started", you agree to our{" "}
            <a href="https://www.impret.com/terms-of-service">Terms of Service</a> and{" "}
            <a href="https://www.impret.com/privacy-policy">Privacy Policy</a>.
          </p>
        </div>
      </div>

      {toast?.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleToastClose}
          duration={3000}
        />
      )}
    </div>
  );
};

export default Signup;
